import { useSuspenseQuery } from '@apollo/client/react';
import { startTransition } from 'react';
import { useNavigate } from 'react-router';

import {
  EscrowInvitationDialog,
  InvitationAccepted,
  InvitationInvalid,
  InvitationPending,
} from 'src/widgets/EscrowInvitationDialog';
import { EscrowInvitationDocument } from 'src/graphql/actions/escrow/escrowInvitation.query';
import { InvitationStatusEnum } from 'src/graphql/schema';
import { useRefetchTasks } from 'src/containers/Tasks';

import { useAcceptRejectEscrowInvitation } from '../api';
import { getEscrowUrl, getInvitationDetails } from '../model';

type Props = {
  invitationId: string;
  onClose: () => void;
};

const InvitationContent = ({ invitationId, onClose }: Props) => {
  const { data, error } = useSuspenseQuery(EscrowInvitationDocument, {
    variables: { id: invitationId },
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });
  const navigate = useNavigate();
  const refetchTasks = useRefetchTasks();

  const goToEscrow = () => {
    if (!data) return;

    startTransition(() => navigate(getEscrowUrl(data.escrowInvitation.escrow)));
  };

  const handleAcceptCompleted = () => {
    refetchTasks();
    goToEscrow();
  };

  const handleRejectCompleted = () => {
    refetchTasks();
    onClose();
  };

  const { accept, reject, acceptLoading, rejectLoading } = useAcceptRejectEscrowInvitation(invitationId, {
    onAcceptCompleted: handleAcceptCompleted,
    onRejectCompleted: handleRejectCompleted,
  });

  if (error || !data || data.escrowInvitation.status === InvitationStatusEnum.Rejected)
    return (
      <EscrowInvitationDialog open onClose={onClose}>
        <InvitationInvalid onClose={onClose} />
      </EscrowInvitationDialog>
    );

  const { escrowInvitation } = data;

  if (escrowInvitation.status === InvitationStatusEnum.Pending)
    return (
      <InvitationPending
        details={getInvitationDetails(escrowInvitation)}
        onAccept={accept}
        onReject={reject}
        onClose={onClose}
        acceptLoading={acceptLoading}
        rejectLoading={rejectLoading}
      />
    );

  return (
    <EscrowInvitationDialog open onClose={onClose}>
      <InvitationAccepted onClose={onClose} goToEscrow={goToEscrow} />
    </EscrowInvitationDialog>
  );
};

export default InvitationContent;
